import {
  File as FileIcon,
  Image as ImageIcon,
  MapPin as MapPinIcon,
  Mic as MicIcon,
  Sticker as StickerIcon,
  User as UserIcon,
  Video as VideoIcon,
  Volume2 as AudioIcon,
  CircleAlert as UnsupportedIcon,
  X as XIcon,
} from "lucide-react";
import { cn } from "../../../lib/utils.js";
import { MessageReplyPreviewThumbnail } from "./MessageReplyPreviewThumbnail.js";
import type { ReplyQuoteKind, ReplyQuotePresentation } from "./reply-quote-model.js";

function mediaIcon(kind: ReplyQuoteKind): React.JSX.Element | null {
  switch (kind) {
    case "image":
      return <ImageIcon size={12} className="shrink-0" aria-hidden="true" />;
    case "video":
      return <VideoIcon size={12} className="shrink-0" aria-hidden="true" />;
    case "voice":
      return <MicIcon size={12} className="shrink-0" aria-hidden="true" />;
    case "audio":
      return <AudioIcon size={12} className="shrink-0" aria-hidden="true" />;
    case "document":
      return <FileIcon size={12} className="shrink-0" aria-hidden="true" />;
    case "sticker":
      return <StickerIcon size={12} className="shrink-0" aria-hidden="true" />;
    case "location":
      return <MapPinIcon size={12} className="shrink-0" aria-hidden="true" />;
    case "contact":
      return <UserIcon size={12} className="shrink-0" aria-hidden="true" />;
    case "unsupported":
      return <UnsupportedIcon size={12} className="shrink-0" aria-hidden="true" />;
    case "text":
    case "other":
      return null;
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}

export function ReplyQuoteStrip({
  presentation,
  organizationId,
  dismissLabel,
  onDismiss,
  className,
}: {
  presentation: ReplyQuotePresentation;
  organizationId?: number;
  dismissLabel?: string;
  onDismiss?: () => void;
  className?: string;
}): React.JSX.Element {
  const accentClass = presentation.isSelf ? "bg-success" : "bg-accent";
  const nameClass = presentation.isSelf ? "text-success" : "text-accent";
  const icon = mediaIcon(presentation.kind);
  const showThumbnail = presentation.kind === "image" || presentation.kind === "sticker";
  const thumbnail =
    showThumbnail && presentation.previewAttachment && organizationId != null ? (
      <MessageReplyPreviewThumbnail
        organizationId={organizationId}
        attachment={presentation.previewAttachment}
        className={
          presentation.kind === "sticker"
            ? "size-11 bg-transparent object-contain ring-0"
            : "size-11"
        }
      />
    ) : null;

  return (
    <div className={cn("flex min-w-0 items-stretch overflow-hidden rounded-md", className)}>
      <span className={cn("w-[3px] shrink-0 self-stretch", accentClass)} aria-hidden="true" />
      <div className="flex min-w-0 flex-1 items-center gap-2 px-2 py-1">
        <div className="min-w-0 flex-1">
          <p className={cn("m-0 truncate text-[0.75rem] font-medium leading-snug", nameClass)}>
            {presentation.authorLabel}
          </p>
          <p className="m-0 flex min-w-0 items-center gap-1 text-[0.8125rem] leading-[1.4] text-ink-muted">
            {icon}
            <span className="min-w-0 truncate wrap-anywhere">{presentation.bodyText}</span>
          </p>
        </div>
        {thumbnail}
        {onDismiss ? (
          <button
            type="button"
            className="shrink-0 rounded p-1 text-ink-subtle hover:bg-surface hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
            aria-label={dismissLabel}
            onClick={onDismiss}
          >
            <XIcon size={14} aria-hidden="true" />
          </button>
        ) : null}
      </div>
    </div>
  );
}
